import { useEffect, useState } from 'react';
import { Row, Input, Drawer, Button, Divider, Flex, Form, Space, theme, Badge, Col } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { ResetButton } from '@shared/components/buttons/ResetButton';
import { SearchButton } from '@shared/components/buttons/SearchButton';
import { FilterField } from './FilterField';
import { FilterGroupField } from './FilterGroupField';

/**
 * Barra de herramientas para aplicar filtros.
 *
 * @param {Object} props
 * @param {Object} props.defaultFilters Filtros por defecto que se utilizan al reiniciar o como fallback si no especifica `initialFilters`.
 * @param {Object} [props.initialFilters] Filtros iniciales al montar el componente. Si no se especifica, se utilizan los `defaultFilters`.
 * @param {Function} [props.onSearch] Callback que se ejecuta al aplicar o reiniciar los filtros.
 * @param {Array} props.items Lista de objetos con la configuración de los elementos a renderizar. Puede ser de los siguientes tipos:
 * * Campo estándar (`type` no establecido)
 * ```
 *   {
 *     component: ReactNode, // Componente de entrada
 *     visible?: boolean, // Si es `false`, el campo no se renderiza
 *     ...rest // Cualquier otra prop que admita `FilterField`
 *   }
 * ```
 * * Título
 * ```
 *   {
 *     type: 'title',
 *     label: string|ReactNode, // Título de la sección
 *     visible?: boolean
 *   }
 * ```
 * * Grupo
 * ```
 *   {
 *     type: 'group',
 *     items: Array<Object>, // Lista de elementos internos (misma estructura que "Campo estándar")
 *     visible?: boolean
 *     ...rest // Cualquier otra prop que admita `FilterGroupField`
 *   }
 * ```
 */
export const FiltersToolbar = ({
  defaultFilters,
  initialFilters,
  onSearch,
  items,
}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [modifiedFilters, setModifiedFilters] = useState(new Set());
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);
  const [form] = Form.useForm();
  const { token: themeToken } = theme.useToken();

  /** Actualiza los filtros modificados y retorna el total de filtros modificados. Se excluye 'searchText' */
  const updateModifiedFilters = (values) => {
    const updatedActiveFilters = Object.entries(values).reduce((acc, [field, currentValue]) => {
      if (field === 'searchText') return acc; // Excluye 'searchText' de los filtros modificados

      const initialValue = defaultFilters[field];

      const isSame = Array.isArray(currentValue)
        ? (currentValue.length === initialValue.length) && (currentValue.every((item) => initialValue.includes(item))) // Compara arrays ignorando el orden
        : currentValue === initialValue;

      if (isSame) {
        acc.delete(field)
      } else {
        acc.add(field)
      }

      return acc;
    }, new Set(modifiedFilters)); // Se utiliza una copia de los filtros modificados actuales como valor inicial

    setModifiedFilters(updatedActiveFilters);
    return updatedActiveFilters.size; // Retorna el tamaño del total de filtros modificados
  };

  /** Actualiza los filtros modificados y el total de filtros aplicados */
  const updateModifiedFiltersAndCount = (values) => {
    const count = updateModifiedFilters(values);
    setAppliedFilterCount(count);
  };

  const handleFinish = (values) => {
    updateModifiedFiltersAndCount(values);
    setShowDrawer(false);
    onSearch?.(values);
  }

  const handleValuesChange = (changedValues) => {
    updateModifiedFilters(changedValues)
  };

  /** Restaura los valores de los filtros del Drawer a sus valores por defecto, sin modificar 'searchText' y sin aplicar los filtros */
  const handleUndoChanges = () => {
    const { searchText, ...restDefaultFilters } = defaultFilters
    form.setFieldsValue(restDefaultFilters);
    handleValuesChange(restDefaultFilters); // setFieldsValue no dispara onValuesChange automáticamente, así se que se lo ejecuta manualmente
  }

  const handleResetFilters = () => {
    form.setFieldsValue(defaultFilters);
    handleFinish(defaultFilters);
  };

  const handleSearch = () => {
    form.submit();
  }

  // Inicializa el formulario con los filtros iniciales o por defecto al montar el componente
  useEffect(() => {
    const startFilters = initialFilters || defaultFilters;
    form.setFieldsValue(startFilters);
    updateModifiedFiltersAndCount(startFilters);
  }, [])

  const renderItem = (item, index) => {
    if (item.visible === false) return null;

    // Se establece la 'key' con base en 'name', 'label' o 'index'
    const key = item.key ?? item.name ?? (typeof item.label === 'string' ? item.label : `item${index}`)

    if (item.type === 'title') {
      return (
        <Col key={key} span={24}>
          <Divider style={{ marginTop: 16, marginBottom: 0, fontWeight: 600 }}>{item.label}</Divider>
        </Col>
      );
    }

    if (item.type === 'group') {
      return (
        <FilterGroupField
          key={key}
          modifiedFilters={modifiedFilters}
          layout='vertical'
          {...item}
        />
      );
    }

    // Si no se establece el 'type' o no coincide con ninguno de los anteriores, se asume que es un campo normal
    return (
      <FilterField
        key={key}
        modifiedFilters={modifiedFilters}
        layout='vertical'
        {...item}
      >
        {item.component}
      </FilterField>
    );
  };

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      layout='vertical'
    >
      <Flex gap='small' align='center' style={{ maxWidth: 800 }}>
        <Form.Item name='searchText' noStyle>
          <Input.Search
            placeholder='Buscar'
            enterButton={<SearchButton />}
            onSearch={handleSearch}
          />
        </Form.Item>
        <Badge count={appliedFilterCount} title='Filtros activos'>
          <Button
            type='primary'
            ghost
            onClick={() => setShowDrawer(true)}
            icon={<FontAwesomeIcon icon={faFilter} />}
          >
            Filtros
          </Button>
        </Badge>
        <Divider type='vertical' style={{ borderInlineStartWidth: 4, height: '1.5em' }} />
        <ResetButton onClick={handleResetFilters} />
        <Drawer
          title={
            <Space>
              <FontAwesomeIcon icon={faFilter} color={themeToken.colorPrimary} />
              Filtros
            </Space>
          }
          extra={
            <Button color='primary' variant='text' onClick={handleUndoChanges}>
              Deshacer cambios
            </Button>
          }
          open={showDrawer}
          closable={false}
          onClose={() => setShowDrawer(false)}
          width='27em'
          footer={(
            <Flex gap='small'>
              <Button onClick={() => setShowDrawer(false)} block>
                Cerrar
              </Button>
              <SearchButton onClick={handleSearch} block />
            </Flex>
          )}
        >
          <Row gutter={[16, 16]}>
            {items.map(renderItem)}
          </Row>
        </Drawer>
      </Flex>
    </Form>
  );
};